import React, {useEffect, useState} from 'react';
import {User} from "types/User";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import Uploader from "../../../components/Form/Uploader";
import {PhotographIcon} from "@heroicons/react/outline";
import {Contact, ContactData} from "../../../types/Contact";
import AgeGroup from "../../../types/AgeGroup";
import Select, {Option} from "../../../components/Form/Select";
import ngage from "../../../services/ngage";
import IconName from "../../../types/IconName";
import {useSearchParams} from "react-router-dom";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";

// Helpers ----

function userOption(user: User): Option {
  return {
    type: 'user',
    id: user.id,
    name: user.name,
    description: user.email,
    icon: IconName.USER
  }
}

// Types ----

type ContactFormProps = {
  contact?: Contact | null
  onSubmit: (values: ContactData, helpers: FormikHelpers<ContactData>) => any
}

// Component ----

export default function ContactForm(props: ContactFormProps) {
  const [searchParams] = useSearchParams();
  const ownerIdParam = searchParams.get("ownerId");
  const ownerId = props.contact?.owner?.id ?? (ownerIdParam ? Number(ownerIdParam) : null);

  const [owner, setOwner] = useState<Option | null>(null);

  // Fetch owner data and set into state
  useEffect(() => {
    if (!ownerId) return;

    ngage.users.get(ownerId)
      .then(user => {
        setOwner(user ? userOption(user) : null)
      })
      .catch(error => console.error(error));
  }, [ownerId])

  async function userSource(query: string) {
    return (await ngage.users.filtered({search: query || null}))?.data.map(userOption) || []
  }

  function handleSelectOwner(owner: Option, formik: FormikProps<ContactData>) {
    void formik.setFieldValue('owner_id', owner?.id ?? 0);
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required("This field is required")
      .max(75, "Maximum of 75 characters"),
    last_name: Yup.string()
      .required("This field is required")
      .max(75, "Maximum of 75 characters"),
    owner_id: Yup.number().required('This field is required'),
  })

  const initialValues: ContactData  = {
    first_name: props.contact?.first_name || '',
    last_name: props.contact?.last_name || '',
    owner_id: ownerId,
    profile_picture_id: props.contact?.profile_picture?.id,
    age_group: props.contact?.age_group || AgeGroup.ADULT,
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Account Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Enter the basic account details for this contact.
                      </p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                      <div className="sm:col-span-3 sm:col-start-1">
                        <div className="mt-1">
                          <Field
                            type="text"
                            name="first_name"
                            id="first_name"
                            label='First name *'
                            hint='75 max chars'
                            maxLength="75"
                            autoFocus
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3 sm:col-start-1">
                        <div className="mt-1">
                          <Field
                            type="text"
                            name="last_name"
                            id="last_name"
                            label='Last name *'
                            hint='75 max chars'
                            maxLength="75"
                          />
                        </div>
                      </div>

                      {/* Owner */}
                      <div className="sm:col-span-3 sm:col-start-1">
                        <Select
                          label="Owner *"
                          hint="Type to search"
                          source={userSource}
                          selected={owner}
                          onChange={selected => handleSelectOwner(selected, formik)}
                          multiple={false}
                        />

                        {(!!formik.errors.owner_id && formik.touched.owner_id) &&
                          <FieldHelp isError>{formik.errors.owner_id}</FieldHelp>
                        }
                      </div>
                    </div>

                    {/* Media */}
                    <div className="pt-8">
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          Media
                        </h3>
                      </div>
                      <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        {/* Files */}
                        <div className="sm:col-span-6">
                          <Uploader
                            id={'profile_picture_id'}
                            name={'profile_picture_id'}
                            icon={PhotographIcon}
                            files={props.contact?.profile_picture}
                            type={Uploader.types.IMAGE}
                            maxFiles={1}
                            onChange={files => formik.setFieldValue('profile_picture_id', files[0]?.id ?? null)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <FormControls formik={formik}/>
            </div>
          </Form>
        )}}
    </Formik>
  );
}